import React, { useMemo, useState } from 'react';
import { Ban, Bot, Pencil, Plus, Power, Search, ShieldAlert } from 'lucide-react';
import { CLIENT_TYPE_OPTIONS, MCP_PUBLIC_URL, clientTypeLabel } from '../governedMcpConstants';
import { useGovernedMcp } from '../governedMcpStore';
import type { McpClient } from '../governedMcpTypes';
import ConfirmDialog from '../components/ConfirmDialog';
import EmptyState from '../components/EmptyState';
import { fieldClass, ghostBtn, primaryBtn } from '../components/GatewayUi';
import { ClientStatusBadge, EnvBadge } from '../components/StatusBadges';
import ClientFormDrawer from '../drawers/ClientFormDrawer';

const TYPE_ICON = Bot;

export default function ClientsTab({
  isLight,
  onToast,
}: {
  isLight: boolean;
  onToast: (type: 'success' | 'error' | 'info', message: string) => void;
}) {
  const { state, saveClient, setClientStatus, deleteClient } = useGovernedMcp();
  const [query, setQuery] = useState('');
  const [editing, setEditing] = useState<McpClient | null | 'new'>(null);
  const [confirm, setConfirm] = useState<{ client: McpClient; action: 'revoke' | 'delete' } | null>(null);
  const occupiedRegistrationIds = useMemo(() => {
    const currentId = editing && editing !== 'new' ? editing.id : null;
    return state.clients
      .filter((client) => client.id !== currentId)
      .map((client) => client.registrationId)
      .filter(Boolean);
  }, [state.clients, editing]);

  const rows = useMemo(() => {
    const q = query.trim().toLowerCase();
    return state.clients.filter((client) => {
      if (!q) return true;
      return `${client.name} ${client.registrationId} ${clientTypeLabel(client.clientType)}`.toLowerCase().includes(q);
    });
  }, [state.clients, query]);

  return (
    <div>
      <div
        className={`rounded-lg border px-4 py-3 mb-4 text-sm ${
          isLight ? 'border-indigo-200 bg-indigo-50 text-indigo-900' : 'border-indigo-500/30 bg-indigo-500/10 text-indigo-100'
        }`}
      >
        <ShieldAlert size={16} className="inline mr-2 -mt-0.5" />
        Selfic never trusts a user identity asserted by Claude, ChatGPT, Copilot, or any other client. The IdP token is mapped
        to the enterprise user, then existing Selfic permissions apply.
      </div>
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <div className="relative flex-1 min-w-[220px]">
          <Search size={15} className={`absolute left-3 top-1/2 -translate-y-1/2 ${isLight ? 'text-neutral-400' : 'text-neutral-500'}`} />
          <input className={`${fieldClass(isLight)} pl-9`} placeholder="Search clients…" value={query} onChange={(e) => setQuery(e.target.value)} />
        </div>
        <button type="button" className={primaryBtn()} onClick={() => setEditing('new')}>
          <Plus size={14} /> Register client
        </button>
      </div>

      {rows.length === 0 ? (
        <EmptyState
          isLight={isLight}
          title="No registered AI clients"
          description="Register Claude Enterprise, ChatGPT, Copilot, or an internal agent against the MCP host."
          action={
            <button type="button" className={primaryBtn()} onClick={() => setEditing('new')}>
              Register client
            </button>
          }
        />
      ) : (
        <div className="grid md:grid-cols-2 gap-4">
          {rows.map((client) => (
            <article
              key={client.id}
              className={`rounded-xl border p-4 flex flex-col ${isLight ? 'bg-white border-neutral-200' : 'bg-neutral-900 border-neutral-800'}`}
            >
              <div className="flex items-start justify-between gap-3">
                <div className="flex items-center gap-3 min-w-0">
                  <div className={`p-2 rounded-lg ${isLight ? 'bg-neutral-100' : 'bg-neutral-800'}`}>
                    <TYPE_ICON size={18} className="text-indigo-400" />
                  </div>
                  <div className="min-w-0">
                    <h3 className={`font-semibold truncate ${isLight ? 'text-neutral-900' : 'text-white'}`}>{client.name}</h3>
                    <p className={`text-xs ${isLight ? 'text-neutral-500' : 'text-neutral-400'}`}>
                      {clientTypeLabel(client.clientType)} · {client.registrationId}
                    </p>
                  </div>
                </div>
                <ClientStatusBadge status={client.status} />
              </div>
              <dl className={`grid grid-cols-2 gap-2 text-xs mt-4 ${isLight ? 'text-neutral-600' : 'text-neutral-400'}`}>
                <div>
                  <dt>MCP URL</dt>
                  <dd className={`font-mono break-all ${isLight ? 'text-neutral-900' : 'text-white'}`}>{MCP_PUBLIC_URL}</dd>
                </div>
                <div>
                  <dt>Environment</dt>
                  <dd>
                    <EnvBadge environment={client.environment} />
                  </dd>
                </div>
                <div>
                  <dt>Trust</dt>
                  <dd className="capitalize">{client.trustPolicy.replace(/_/g, ' ')}</dd>
                </div>
                <div>
                  <dt>Last connected</dt>
                  <dd>{client.lastConnectedAt ? new Date(client.lastConnectedAt).toLocaleString() : 'Never'}</dd>
                </div>
                <div className="col-span-2">
                  <dt>Auth</dt>
                  <dd>{client.authConfig}</dd>
                </div>
                <div>
                  <dt>Created by</dt>
                  <dd>{client.createdBy}</dd>
                </div>
              </dl>
              <div className="flex flex-wrap gap-2 mt-4">
                <button type="button" className={ghostBtn(isLight)} onClick={() => setEditing(client)}>
                  <Pencil size={14} /> Edit
                </button>
                <button
                  type="button"
                  className={ghostBtn(isLight)}
                  onClick={() => {
                    const next = client.status === 'active' ? 'disabled' : 'active';
                    setClientStatus(client.id, next);
                    onToast('success', next === 'active' ? 'Client enabled' : 'Client disabled');
                  }}
                >
                  <Power size={14} /> {client.status === 'active' ? 'Disable' : 'Enable'}
                </button>
                <button type="button" className={ghostBtn(isLight) + ' text-red-400'} onClick={() => setConfirm({ client, action: 'revoke' })}>
                  <Ban size={14} /> Revoke
                </button>
              </div>
            </article>
          ))}
        </div>
      )}

      <p className={`text-xs mt-4 ${isLight ? 'text-neutral-500' : 'text-neutral-500'}`}>
        Supported examples: {CLIENT_TYPE_OPTIONS.map((option) => option.label).join(' · ')}
      </p>

      <ClientFormDrawer
        open={editing !== null}
        initial={editing && editing !== 'new' ? editing : null}
        occupiedRegistrationIds={occupiedRegistrationIds}
        isLight={isLight}
        onClose={() => setEditing(null)}
        onSave={(client) => {
          saveClient(client);
          setEditing(null);
          onToast('success', `Client “${client.name}” is available when creating policies.`);
        }}
      />
      <ConfirmDialog
        open={Boolean(confirm)}
        title={confirm?.action === 'revoke' ? 'Revoke client immediately?' : 'Delete client?'}
        description={
          confirm?.action === 'revoke'
            ? `${confirm.client.name} will be unable to discover or invoke any capability.`
            : 'This removes the registration from the gateway.'
        }
        confirmLabel={confirm?.action === 'revoke' ? 'Revoke now' : 'Delete'}
        danger
        isLight={isLight}
        onCancel={() => setConfirm(null)}
        onConfirm={() => {
          if (!confirm) return;
          if (confirm.action === 'revoke') {
            setClientStatus(confirm.client.id, 'revoked');
            onToast('success', 'Client revoked');
          } else {
            deleteClient(confirm.client.id);
            onToast('success', 'Client deleted');
          }
          setConfirm(null);
        }}
      />
    </div>
  );
}
